import React, { useEffect, useMemo, useState } from "react";
import API from "./api";
import { formatProfileNumberValue } from "./profileNumber";

const PAGE_SIZE = 24;
const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "potential", label: "Potential" },
  { value: "contacted", label: "Contacted" },
  { value: "follow_up", label: "Follow Up" },
  { value: "eligible", label: "Eligible" },
  { value: "converted", label: "Converted" },
  { value: "closed", label: "Closed" },
];

export default function FutureProspects({
  onBack,
  onViewProfile,
  canViewProfile = true,
  canUpdateFutureProspects = false,
}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ due: 0, eligible_now: 0, converted: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    q: "",
    submittedQ: "",
    status: "",
  });
  const [drafts, setDrafts] = useState({});
  const [fieldEditor, setFieldEditor] = useState({
    open: false,
    mode: "",
    item: null,
    birth_date: "",
    expected_eligible_year: "",
  });

  useEffect(() => {
    loadFutureProspects(currentPage, filters.submittedQ, filters.status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters.submittedQ, filters.status]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const headerStats = useMemo(
    () => [
      { label: "Total Prospects", value: total, tone: "primary" },
      { label: "Due Follow-up", value: stats.due, tone: "warn" },
      { label: "Eligible Now", value: stats.eligible_now, tone: "accent" },
      { label: "Converted", value: stats.converted, tone: "success" },
    ],
    [stats, total]
  );

  async function loadFutureProspects(page = 1, q = "", status = "") {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(Math.max(0, (page - 1) * PAGE_SIZE)),
      });
      if (q) params.set("q", q);
      if (status) params.set("status", status);

      const res = await API.get(`/future-prospects?${params.toString()}`);
      const payload = res.data || {};
      const nextItems = Array.isArray(payload.items) ? payload.items : [];
      const nextTotal = Number(payload.total || 0);
      const nextTotalPages = Math.max(1, Math.ceil(nextTotal / PAGE_SIZE));
      if (page > nextTotalPages) {
        setCurrentPage(nextTotalPages);
        return;
      }

      setItems(nextItems);
      setTotal(nextTotal);
      setStats({
        due: Number(payload.stats?.due || 0),
        eligible_now: Number(payload.stats?.eligible_now || 0),
        converted: Number(payload.stats?.converted || 0),
      });
      setDrafts((current) => {
        const next = { ...current };
        nextItems.forEach((item) => {
          const key = String(item.id);
          next[key] = {
            prospect_status: current[key]?.prospect_status || item.prospect_status || "potential",
            expected_eligible_year:
              current[key]?.expected_eligible_year !== undefined
                ? current[key].expected_eligible_year
                : String(item.expected_eligible_year || ""),
            follow_up_at:
              current[key]?.follow_up_at !== undefined
                ? current[key].follow_up_at
                : toDateTimeLocalValue(item.follow_up_at),
            notes: current[key]?.notes !== undefined ? current[key].notes : (item.notes || ""),
          };
        });
        return next;
      });
    } catch (err) {
      console.error("Failed to load future prospects", err);
      setError(err.response?.data?.error || "Failed to load future prospects.");
      setItems([]);
      setTotal(0);
      setStats({ due: 0, eligible_now: 0, converted: 0 });
    } finally {
      setLoading(false);
    }
  }

  async function saveProspect(item) {
    if (!canUpdateFutureProspects) return;
    const draft = drafts[String(item.id)] || {};
    setSavingId(item.id);
    setError("");
    try {
      await API.patch(`/future-prospects/${item.id}`, {
        prospect_status: draft.prospect_status || item.prospect_status || "potential",
        expected_eligible_year: normalizeYearInput(draft.expected_eligible_year),
        follow_up_at: fromDateTimeLocalValue(draft.follow_up_at),
        notes: draft.notes || "",
      });
      await loadFutureProspects(currentPage, filters.submittedQ, filters.status);
    } catch (err) {
      console.error("Failed to update future prospect", err);
      setError(err.response?.data?.error || "Failed to update future prospect.");
    } finally {
      setSavingId(null);
    }
  }

  async function updateProspectBasics() {
    if (!canUpdateFutureProspects || !fieldEditor.item?.id) return;
    setSavingId(fieldEditor.item.id);
    setError("");
    try {
      const payload =
        fieldEditor.mode === "dob"
          ? {
              birth_date: fromDateInputValue(fieldEditor.birth_date),
            }
          : {
              expected_eligible_year: normalizeYearInput(fieldEditor.expected_eligible_year),
            };
      await API.patch(`/future-prospects/${fieldEditor.item.id}`, payload);
      if (fieldEditor.mode === "eligible_year") {
        setDrafts((current) => ({
          ...current,
          [fieldEditor.item.id]: {
            ...current[fieldEditor.item.id],
            expected_eligible_year: fieldEditor.expected_eligible_year,
          },
        }));
      }
      setFieldEditor({
        open: false,
        mode: "",
        item: null,
        birth_date: "",
        expected_eligible_year: "",
      });
      await loadFutureProspects(currentPage, filters.submittedQ, filters.status);
    } catch (err) {
      console.error("Failed to update future prospect basics", err);
      setError(err.response?.data?.error || "Failed to update future prospect.");
    } finally {
      setSavingId(null);
    }
  }

  function openFieldEditor(mode, item, draft) {
    setFieldEditor({
      open: true,
      mode,
      item,
      birth_date: toDateInputValue(item.birth_date),
      expected_eligible_year: draft?.expected_eligible_year ?? String(item.expected_eligible_year || ""),
    });
  }

  return (
    <div style={S.page}>
      <style>{`
        @media (max-width: 980px) {
          .jk-fp-hero { flex-direction: column !important; align-items: flex-start !important; }
          .jk-fp-filter-grid { grid-template-columns: 1fr !important; }
          .jk-fp-stat-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
        @media (max-width: 720px) {
          .jk-fp-stat-grid { grid-template-columns: 1fr !important; }
          .jk-fp-card-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div style={S.hero} className="jk-fp-hero">
        <div>
          <p style={S.eyebrow}>Sibling Prospect Tracker</p>
          <h1 style={S.title}>Future Prospects</h1>
          <p style={S.subtitle}>
            Review relatives who may become eligible later, track follow-ups, and open linked profiles directly.
          </p>
        </div>

        <div style={S.heroActions}>
          <button type="button" style={S.backBtn} onClick={onBack}>
            Back to Search
          </button>
        </div>
      </div>

      <div style={S.statGrid} className="jk-fp-stat-grid">
        {headerStats.map((item) => (
          <div key={item.label} style={S.statCard}>
            <div style={{ ...S.statDot, ...(item.tone === "warn" ? S.statDotWarn : item.tone === "success" ? S.statDotSuccess : item.tone === "accent" ? S.statDotAccent : {}) }} />
            <div>
              <div style={S.statValue}>{item.value}</div>
              <div style={S.statLabel}>{item.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={S.filterCard}>
        <div style={S.filterGrid} className="jk-fp-filter-grid">
          <div style={S.filterField}>
            <label style={S.filterLabel}>Search</label>
            <input
              style={S.input}
              value={filters.q}
              onChange={(event) => setFilters((current) => ({ ...current, q: event.target.value }))}
              placeholder="Profile no, parent name, relative name"
            />
          </div>

          <div style={S.filterField}>
            <label style={S.filterLabel}>Status</label>
            <select
              style={S.select}
              value={filters.status}
              onChange={(event) => {
                setCurrentPage(1);
                setFilters((current) => ({ ...current, status: event.target.value }));
              }}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

        </div>

        <div style={S.filterActions}>
          <button
            type="button"
            style={S.primaryBtn}
            onClick={() => {
              setCurrentPage(1);
              setFilters((current) => ({ ...current, submittedQ: current.q.trim() }));
            }}
          >
            Search
          </button>
          <button
            type="button"
            style={S.secondaryBtn}
            onClick={() => {
              setCurrentPage(1);
              setFilters({ q: "", submittedQ: "", status: "" });
            }}
          >
            Reset
          </button>
        </div>
      </div>

      {error && <div style={S.errorBox}>{error}</div>}

      {fieldEditor.open && (
        <div
          style={S.modalBackdrop}
          onClick={() => setFieldEditor({ open: false, mode: "", item: null, birth_date: "", expected_eligible_year: "" })}
        >
          <div style={S.modalCard} onClick={(event) => event.stopPropagation()}>
            <div style={S.modalHeader}>
              <div>
                <div style={S.modalEyebrow}>Future Prospect</div>
                <h3 style={S.modalTitle}>
                  {fieldEditor.mode === "dob" ? "Edit DOB" : "Edit Eligible Year"}
                </h3>
                <p style={S.modalSubtitle}>
                  {fieldEditor.item?.relative_name || "Relative"}
                </p>
              </div>
              <button
                type="button"
                style={S.modalCloseBtn}
                onClick={() => setFieldEditor({ open: false, mode: "", item: null, birth_date: "", expected_eligible_year: "" })}
                aria-label="Close"
              >
                x
              </button>
            </div>

            <div style={S.modalSingleField}>
              {fieldEditor.mode === "dob" ? (
                <div>
                  <label style={S.inputLabel}>DOB</label>
                  <input
                    style={S.input}
                    type="date"
                    value={fieldEditor.birth_date}
                    onChange={(event) =>
                      setFieldEditor((current) => ({
                        ...current,
                        birth_date: event.target.value,
                      }))
                    }
                  />
                </div>
              ) : (
                <div>
                  <label style={S.inputLabel}>Eligible Year</label>
                  <input
                    style={S.input}
                    type="number"
                    min="1900"
                    max="9999"
                    value={fieldEditor.expected_eligible_year}
                    onChange={(event) =>
                      setFieldEditor((current) => ({
                        ...current,
                        expected_eligible_year: event.target.value,
                      }))
                    }
                  />
                </div>
              )}
            </div>

            <div style={S.modalActions}>
              <button
                type="button"
                style={S.secondaryBtn}
                onClick={() => setFieldEditor({ open: false, mode: "", item: null, birth_date: "", expected_eligible_year: "" })}
              >
                Cancel
              </button>
              <button
                type="button"
                style={S.primaryBtn}
                onClick={updateProspectBasics}
                disabled={savingId === fieldEditor.item?.id}
              >
                {savingId === fieldEditor.item?.id ? "Updating..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <Splash title="Loading future prospects" subtitle="Fetching sibling prospect records and follow-up details." />
      ) : items.length === 0 ? (
        <Splash title="No future prospects found" subtitle="Try changing the filters or create future prospect relatives first." />
      ) : (
        <>
          <div style={S.cardGrid} className="jk-fp-card-grid">
            {items.map((item) => {
              const draft = drafts[String(item.id)] || {};
              return (
                <div key={item.id} style={S.card}>
                  <div style={S.cardTop}>
                    <div>
                      <h3 style={S.cardTitle}>{item.relative_name || "Unnamed Relative"}</h3>
                      <div style={S.cardSub}>
                        {[labelize(item.relation_type), item.relative_gender, item.parent_profile_no ? `Linked #${formatProfileNumberValue(item.parent_profile_no)}` : ""]
                          .filter(Boolean)
                          .join(" | ")}
                      </div>
                    </div>
                    <span style={{ ...S.statusBadge, ...statusBadgeStyle(item.prospect_status) }}>
                      {labelize(item.prospect_status)}
                    </span>
                  </div>

                  <div style={S.metaGrid}>
                    <Info label="Linked Profile" value={item.parent_profile_name || "-"} />
                    <Info
                      label="DOB"
                      value={formatDate(item.birth_date)}
                      onEdit={
                        canUpdateFutureProspects
                          ? () => openFieldEditor("dob", item, draft)
                          : null
                      }
                    />
                    <Info label="Age" value={formatAge(item.birth_date)} />
                    <Info
                      label="Eligible Year"
                      value={draft.expected_eligible_year || item.expected_eligible_year || "-"}
                      onEdit={
                        canUpdateFutureProspects
                          ? () => openFieldEditor("eligible_year", item, draft)
                          : null
                      }
                    />
                    <Info label="Follow-up" value={formatDateTime(item.follow_up_at)} />
                    <Info label="Converted Profile" value={item.converted_profile_no ? `#${formatProfileNumberValue(item.converted_profile_no)} ${item.converted_profile_name || ""}`.trim() : "Not converted"} />
                  </div>

                  {canUpdateFutureProspects ? (
                    <div style={S.editorBox}>
                      <div style={S.editorGrid}>
                        <div>
                          <label style={S.inputLabel}>Status</label>
                          <select
                            style={S.select}
                            value={draft.prospect_status || "potential"}
                            onChange={(event) =>
                              setDrafts((current) => ({
                                ...current,
                                [item.id]: {
                                  ...current[item.id],
                                  prospect_status: event.target.value,
                                },
                              }))
                            }
                          >
                            {STATUS_OPTIONS.filter((option) => option.value).map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label style={S.inputLabel}>Follow-up At</label>
                          <input
                            style={S.input}
                            type="datetime-local"
                            value={draft.follow_up_at || ""}
                            onChange={(event) =>
                              setDrafts((current) => ({
                                ...current,
                                [item.id]: {
                                  ...current[item.id],
                                  follow_up_at: event.target.value,
                                },
                              }))
                            }
                          />
                        </div>
                      </div>

                      <div>
                        <label style={S.inputLabel}>Notes</label>
                        <textarea
                          style={S.textarea}
                          rows={3}
                          value={draft.notes || ""}
                          onChange={(event) =>
                            setDrafts((current) => ({
                              ...current,
                              [item.id]: {
                                ...current[item.id],
                                notes: event.target.value,
                              },
                            }))
                          }
                        />
                      </div>
                    </div>
                  ) : item.notes ? (
                    <div style={S.notesBox}>
                      <div style={S.inputLabel}>Notes</div>
                      <div style={S.notesText}>{item.notes}</div>
                    </div>
                  ) : null}

                  <div style={S.actionRow}>
                    {canViewProfile && item.profile_id && (
                      <button type="button" style={S.linkBtn} onClick={() => onViewProfile?.(item.profile_id)}>
                        Open Linked Profile
                      </button>
                    )}
                    {canViewProfile && item.converted_profile_id && (
                      <button type="button" style={S.linkBtn} onClick={() => onViewProfile?.(item.converted_profile_id)}>
                        Open Converted Profile
                      </button>
                    )}
                    {canUpdateFutureProspects && (
                      <button
                        type="button"
                        style={S.primaryBtn}
                        onClick={() => saveProspect(item)}
                        disabled={savingId === item.id}
                      >
                        {savingId === item.id ? "Saving..." : "Save"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={S.paginationBar}>
            <button
              type="button"
              style={{ ...S.paginationBtn, ...(currentPage <= 1 ? S.paginationBtnDisabled : {}) }}
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            >
              Previous
            </button>
            <span style={S.paginationInfo}>
              Page {currentPage} of {totalPages} | {total} prospects
            </span>
            <button
              type="button"
              style={{ ...S.paginationBtn, ...(currentPage >= totalPages ? S.paginationBtnDisabled : {}) }}
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Info({ label, value, onEdit = null }) {
  return (
    <div style={S.infoItem}>
      <div style={S.infoTopRow}>
        <div style={S.infoLabel}>{label}</div>
        {typeof onEdit === "function" ? (
          <button type="button" style={S.infoEditBtn} onClick={onEdit}>
            Edit
          </button>
        ) : null}
      </div>
      <div style={S.infoValue}>{value || "-"}</div>
    </div>
  );
}

function Splash({ title, subtitle }) {
  return (
    <div style={S.splash}>
      <h3 style={S.splashTitle}>{title}</h3>
      <p style={S.splashText}>{subtitle}</p>
    </div>
  );
}

function labelize(value) {
  return String(value || "")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function formatDateTime(value) {
  if (!value) return "Not scheduled";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not scheduled";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAge(value) {
  if (!value) return "-";
  const dob = new Date(value);
  if (Number.isNaN(dob.getTime())) return "-";
  return `${Math.floor((Date.now() - dob.getTime()) / (365.25 * 86400000))} yrs`;
}

function toDateTimeLocalValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  const normalized = new Date(date.getTime() - offset * 60000);
  return normalized.toISOString().slice(0, 16);
}

function toDateInputValue(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function fromDateInputValue(value) {
  if (!value) return null;
  const normalized = String(value).trim().slice(0, 10);
  return normalized || null;
}

function fromDateTimeLocalValue(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

function normalizeYearInput(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.round(parsed);
}

function statusBadgeStyle(status) {
  const key = String(status || "").trim().toLowerCase();
  if (key === "converted") return S.statusConverted;
  if (key === "eligible") return S.statusEligible;
  if (key === "closed") return S.statusClosed;
  if (key === "contacted" || key === "follow_up") return S.statusFollowUp;
  return S.statusPotential;
}

const C = {
  bg: "#fff9f7",
  card: "#ffffff",
  border: "rgba(185,52,58,0.14)",
  accent: "#D73C2E",
  accentDark: "#B9343A",
  text: "#252935",
  muted: "#6b6f7d",
  soft: "#fff3ef",
  success: "#1f7a4d",
  successSoft: "#eef8f2",
  warn: "#b56a00",
  warnSoft: "#fff5e6",
};

const S = {
  page: {
    minHeight: "calc(100vh - 60px)",
    background:
      "radial-gradient(circle at 10% 0%, rgba(215,60,46,0.10) 0, transparent 30%), linear-gradient(180deg, #fffefc 0%, #fff8f6 50%, #fff9f7 100%)",
    padding: "20px 20px 48px",
  },
  hero: {
    display: "flex",
    justifyContent: "space-between",
    gap: 20,
    alignItems: "flex-start",
    marginBottom: 22,
  },
  eyebrow: {
    margin: "0 0 8px",
    fontSize: 11,
    letterSpacing: 2,
    textTransform: "uppercase",
    color: C.accentDark,
    fontWeight: 900,
  },
  title: {
    margin: "0 0 8px",
    fontSize: 34,
    lineHeight: 1.08,
    color: C.text,
    fontWeight: 950,
  },
  subtitle: {
    margin: 0,
    maxWidth: 720,
    color: C.muted,
    fontSize: 14,
    lineHeight: 1.6,
  },
  heroActions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  backBtn: {
    minHeight: 42,
    padding: "0 18px",
    borderRadius: 999,
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.text,
    cursor: "pointer",
    fontWeight: 900,
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 14,
    marginBottom: 18,
  },
  statCard: {
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 22,
    padding: 18,
    display: "flex",
    alignItems: "center",
    gap: 12,
    boxShadow: "0 14px 34px rgba(185,52,58,0.08)",
  },
  statDot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    background: C.accent,
    flexShrink: 0,
  },
  statDotWarn: { background: C.warn },
  statDotSuccess: { background: C.success },
  statDotAccent: { background: C.accentDark },
  statValue: {
    color: C.text,
    fontSize: 28,
    fontWeight: 950,
    lineHeight: 1,
  },
  statLabel: {
    marginTop: 6,
    color: C.muted,
    fontSize: 13,
    fontWeight: 800,
  },
  filterCard: {
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 24,
    padding: 18,
    marginBottom: 18,
    boxShadow: "0 16px 40px rgba(185,52,58,0.08)",
  },
  filterGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 2fr) minmax(220px, 1fr) minmax(160px, auto)",
    gap: 14,
    alignItems: "end",
  },
  filterField: {
    display: "flex",
    flexDirection: "column",
    gap: 7,
  },
  filterLabel: {
    color: C.accentDark,
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 1.2,
    textTransform: "uppercase",
  },
  checkboxRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    color: C.text,
    fontSize: 13,
    fontWeight: 800,
  },
  filterActions: {
    display: "flex",
    gap: 10,
    marginTop: 14,
    flexWrap: "wrap",
  },
  input: {
    width: "100%",
    minHeight: 42,
    padding: "0 12px",
    borderRadius: 14,
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.text,
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    minHeight: 42,
    padding: "0 12px",
    borderRadius: 14,
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.text,
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%",
    padding: 12,
    borderRadius: 14,
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.text,
    boxSizing: "border-box",
    resize: "vertical",
    fontFamily: "inherit",
  },
  primaryBtn: {
    minHeight: 42,
    padding: "0 18px",
    borderRadius: 999,
    border: "none",
    background: `linear-gradient(135deg, ${C.accent}, ${C.accentDark})`,
    color: "#fff",
    cursor: "pointer",
    fontWeight: 900,
  },
  secondaryBtn: {
    minHeight: 42,
    padding: "0 18px",
    borderRadius: 999,
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.text,
    cursor: "pointer",
    fontWeight: 900,
  },
  errorBox: {
    padding: 14,
    borderRadius: 16,
    border: "1px solid rgba(185,52,58,0.18)",
    background: "#fff2ef",
    color: C.accentDark,
    marginBottom: 16,
    fontWeight: 800,
  },
  splash: {
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 24,
    padding: "42px 24px",
    textAlign: "center",
  },
  splashTitle: {
    margin: "0 0 8px",
    color: C.text,
    fontSize: 24,
    fontWeight: 950,
  },
  splashText: {
    margin: 0,
    color: C.muted,
    fontSize: 14,
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 16,
  },
  card: {
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 24,
    padding: 18,
    boxShadow: "0 16px 40px rgba(185,52,58,0.08)",
    display: "grid",
    gap: 14,
  },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
  },
  cardTitle: {
    margin: 0,
    color: C.text,
    fontSize: 20,
    fontWeight: 950,
  },
  cardSub: {
    marginTop: 6,
    color: C.muted,
    fontSize: 13,
    fontWeight: 800,
  },
  statusBadge: {
    padding: "7px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 950,
    flexShrink: 0,
  },
  statusPotential: {
    background: C.soft,
    color: C.accentDark,
  },
  statusFollowUp: {
    background: C.warnSoft,
    color: C.warn,
  },
  statusEligible: {
    background: "#fff0f1",
    color: C.accent,
  },
  statusConverted: {
    background: C.successSoft,
    color: C.success,
  },
  statusClosed: {
    background: "#f3f4f7",
    color: C.muted,
  },
  metaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },
  infoItem: {
    padding: 12,
    borderRadius: 16,
    background: "#fffaf8",
    border: `1px solid ${C.border}`,
  },
  infoTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 6,
  },
  infoLabel: {
    color: C.muted,
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 1.1,
  },
  infoEditBtn: {
    border: "none",
    background: "transparent",
    color: C.accent,
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 0.8,
  },
  infoValue: {
    color: C.text,
    fontSize: 14,
    fontWeight: 800,
    lineHeight: 1.45,
    wordBreak: "break-word",
  },
  editorBox: {
    borderTop: `1px solid ${C.border}`,
    paddingTop: 14,
    display: "grid",
    gap: 12,
  },
  editorGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },
  inputLabel: {
    display: "block",
    color: C.accentDark,
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 1.1,
    marginBottom: 7,
  },
  notesBox: {
    borderTop: `1px solid ${C.border}`,
    paddingTop: 14,
  },
  notesText: {
    color: C.text,
    fontSize: 14,
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
  },
  actionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    alignItems: "center",
  },
  linkBtn: {
    minHeight: 40,
    padding: "0 16px",
    borderRadius: 999,
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.accentDark,
    cursor: "pointer",
    fontWeight: 900,
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(37,41,53,0.36)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    zIndex: 40,
  },
  modalCard: {
    width: "100%",
    maxWidth: 520,
    background: "#fff",
    borderRadius: 24,
    border: `1px solid ${C.border}`,
    boxShadow: "0 24px 60px rgba(37,41,53,0.20)",
    padding: 20,
    display: "grid",
    gap: 16,
  },
  modalHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  modalEyebrow: {
    color: C.accentDark,
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 1.1,
    marginBottom: 8,
  },
  modalTitle: {
    margin: 0,
    color: C.text,
    fontSize: 22,
    fontWeight: 950,
  },
  modalSubtitle: {
    margin: "6px 0 0",
    color: C.muted,
    fontSize: 13,
    fontWeight: 700,
  },
  modalCloseBtn: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.accentDark,
    cursor: "pointer",
    fontWeight: 900,
    flexShrink: 0,
  },
  modalSingleField: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: 12,
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
  },
  paginationBar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    justifyContent: "space-between",
    marginTop: 20,
  },
  paginationBtn: {
    minHeight: 42,
    padding: "0 18px",
    borderRadius: 999,
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.text,
    cursor: "pointer",
    fontWeight: 900,
  },
  paginationBtnDisabled: {
    opacity: 0.45,
    cursor: "not-allowed",
  },
  paginationInfo: {
    color: C.muted,
    fontSize: 13,
    fontWeight: 800,
    textAlign: "center",
    flex: 1,
  },
};
